// The client's pages: pay, quote, review, report, save a card, My Trades, privacy, Connect Claude. No sign in, outside the app.
import { useQuery } from "@tanstack/react-query";
import { createRoute, Navigate, useNavigate } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { api } from "../../api/client";
import { Loading } from "../../components/parts";
import { ConnectClaude } from "../../screens/ConnectClaude";
import { MyTrades, MyTradesAsClient } from "../../screens/MyTrades";
import { Privacy } from "../../screens/Privacy";
import { PayPage } from "../../screens/public/PayPage";
import { PublicQuotePage } from "../../screens/public/QuotePage";
import { ReviewPage } from "../../screens/public/ReviewPage";
import { CardSavedPage, SaveCardPage } from "../../screens/public/SaveCard";
import { VisitReportPage } from "../../screens/public/VisitReport";
import { Unsubscribe } from "../../screens/Unsubscribe";
import { ApplyBookingPage, CareersPageView } from "../../screens/Careers";
import { TrialScoreView } from "../../screens/ApplyJourney";
import { rootRoute } from "./base";

/** An old long link code (the old Steward's, or this app's first). These links now go on to the short link. */
const isOldCode = (token: string) => /^([a-f0-9]{16}|[a-f0-9]{32}|[a-f0-9]{64})$/.test(token);

/**
 * A link sent with an old long code goes on to its short link (/i/, /q/, /r/), so the address bar and anything copied
 * from it stay short. Not found: the page itself says the link is wrong.
 */
function ShortLink({ kind, token, children }: { kind: "invoice" | "quote" | "review"; token: string; children: ReactNode }) {
  const old = isOldCode(token);
  const short = useQuery({
    queryKey: ["short-code", kind, token],
    queryFn: () => api<{ token: string }>("GET", `public/link/${kind}/${token}`),
    enabled: old,
    retry: false,
  });
  if (!old || short.isError) return children;
  if (!short.data) return <Loading />;
  const to = kind === "invoice" ? "/i/$token" : kind === "quote" ? "/q/$token" : "/r/$token";
  return <Navigate to={to} params={{ token: short.data.token }} replace />;
}

// The client's pay and quote pages: no sign in, outside the app.
const payPage = createRoute({
  getParentRoute: () => rootRoute,
  path: "/pay/$token",
  validateSearch: (s: Record<string, unknown>): { session?: string } =>
    typeof s.payment_intent === "string" && s.payment_intent ? { session: s.payment_intent } : {},
  component: function PayRoute() {
    const { token } = payPage.useParams();
    const { session } = payPage.useSearch();
    return (
      <ShortLink kind="invoice" token={token}>
        <PayPage key={token} token={token} {...(session ? { session } : {})} />
      </ShortLink>
    );
  },
});

// The short pay link the server sends now (/i/<token>, owner Oct 31). The same page, so Stripe's return lands back here.
const payShort = createRoute({
  getParentRoute: () => rootRoute,
  path: "/i/$token",
  validateSearch: (s: Record<string, unknown>): { session?: string } =>
    typeof s.payment_intent === "string" && s.payment_intent ? { session: s.payment_intent } : {},
  component: function PayShortRoute() {
    const { token } = payShort.useParams();
    const { session } = payShort.useSearch();
    return <PayPage key={token} token={token} {...(session ? { session } : {})} />;
  },
});

// Pay links sent by the old WordPress Steward (/invoice/<code>/, and /gm-invoice/<code>/ before 2.95.0) keep working:
// the copy keeps each invoice's old code (draft 15), and /pay/ sends it on to the short link.
const oldInvoiceLink = createRoute({
  getParentRoute: () => rootRoute,
  path: "/invoice/$token",
  component: function OldInvoiceRoute() {
    const { token } = oldInvoiceLink.useParams();
    return <Navigate to="/pay/$token" params={{ token }} replace />;
  },
});

const olderInvoiceLink = createRoute({
  getParentRoute: () => rootRoute,
  path: "/gm-invoice/$token",
  component: function OlderInvoiceRoute() {
    const { token } = olderInvoiceLink.useParams();
    return <Navigate to="/pay/$token" params={{ token }} replace />;
  },
});

// The client's review page from a review request (old Steward links /review/<code>/ keep working: the copy keeps the code).
const reviewPage = createRoute({
  getParentRoute: () => rootRoute,
  path: "/review/$token",
  component: function ReviewRoute() {
    const { token } = reviewPage.useParams();
    return (
      <ShortLink kind="review" token={token}>
        <ReviewPage key={token} token={token} />
      </ShortLink>
    );
  },
});

// The short review link the server sends now (/r/<token>).
const reviewShort = createRoute({
  getParentRoute: () => rootRoute,
  path: "/r/$token",
  component: function ReviewShortRoute() {
    const { token } = reviewShort.useParams();
    return <ReviewPage key={token} token={token} />;
  },
});

// Quote links from before 2.95.0 (/gm-quote/<code>/).
const olderQuoteLink = createRoute({
  getParentRoute: () => rootRoute,
  path: "/gm-quote/$token",
  component: function OlderQuoteRoute() {
    const { token } = olderQuoteLink.useParams();
    return <Navigate to="/quote/$token" params={{ token }} replace />;
  },
});

// Back from Stripe's page after a card link (Clients, Send card link).
const cardSaved = createRoute({
  getParentRoute: () => rootRoute,
  path: "/card-saved",
  validateSearch: (s: Record<string, unknown>): { back?: string } => (s.back ? { back: "1" } : {}),
  component: function CardSavedRoute() {
    const { back } = cardSaved.useSearch();
    return <CardSavedPage saved={!back} />;
  },
});

// Our own save a card page, from the office's Text them a link to save a card.
const saveCard = createRoute({
  getParentRoute: () => rootRoute,
  path: "/card/$token",
  component: function SaveCardRoute() {
    const { token } = saveCard.useParams();
    return <SaveCardPage key={token} token={token} />;
  },
});

const reportPublic = createRoute({
  getParentRoute: () => rootRoute,
  path: "/report/$token",
  component: function ReportRoute(): ReactNode {
    const { token } = reportPublic.useParams();
    return <VisitReportPage key={token} token={token} />;
  },
});

const quotePublic = createRoute({
  getParentRoute: () => rootRoute,
  path: "/quote/$token",
  component: function QuotePublicRoute() {
    const { token } = quotePublic.useParams();
    return (
      <ShortLink kind="quote" token={token}>
        <PublicQuotePage key={token} token={token} />
      </ShortLink>
    );
  },
});

// The office's look at the client's quote page before sending, drafts too (signed in).
const quotePreview = createRoute({
  getParentRoute: () => rootRoute,
  path: "/preview/quote/$id",
  component: function QuotePreviewRoute() {
    const { id } = quotePreview.useParams();
    return <PublicQuotePage key={id} token="" previewId={id} />;
  },
});

// The short quote link the server sends now (/q/<token>).
const quoteShort = createRoute({
  getParentRoute: () => rootRoute,
  path: "/q/$token",
  component: function QuoteShortRoute() {
    const { token } = quoteShort.useParams();
    return <PublicQuotePage key={token} token={token} />;
  },
});

const myTrades = createRoute({ getParentRoute: () => rootRoute, path: "/my", component: MyTrades });

/** The unsubscribe link in campaign emails: public, no sign in. */
const unsubscribe = createRoute({ getParentRoute: () => rootRoute, path: "/my/unsubscribe", component: Unsubscribe });

/** View as client: full screen like the client's own hub, outside the office menu. The server checks client.view_hub. */
const asClient = createRoute({
  getParentRoute: () => rootRoute,
  path: "/clients/$id/as-client",
  component: function AsClientRoute(): ReactNode {
    const { id } = asClient.useParams();
    const navigate = useNavigate();
    return <MyTradesAsClient key={id} clientId={id} onBack={() => void navigate({ to: "/clients/$id", params: { id } })} />;
  },
});

// Hiring v2 (2026-11-04): the brand careers page (?src= where they found us, ?ref= the crew member who sent them, ?lang=es)
// and the page from our text to pick a phone call time.
const careersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/apply/$slug",
  validateSearch: (s: Record<string, unknown>): { src?: string; ref?: string; lang?: string } => ({
    ...(typeof s.src === "string" ? { src: s.src } : {}),
    ...(typeof s.ref === "string" ? { ref: s.ref } : {}),
    ...(typeof s.lang === "string" ? { lang: s.lang } : {}),
  }),
  component: function CareersRoute(): ReactNode {
    const { slug } = careersRoute.useParams();
    const q = careersRoute.useSearch();
    return <CareersPageView key={slug} slug={slug} {...q} />;
  },
});
// One role's job page (Hiring phase 3): Google Jobs sends people here, with that role picked.
const careersRoleRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/apply/$slug/$role",
  validateSearch: careersRoute.options.validateSearch,
  component: function CareersRoleRoute(): ReactNode {
    const { slug, role } = careersRoleRoute.useParams();
    const q = careersRoleRoute.useSearch();
    return <CareersPageView key={`${slug}/${role}`} slug={slug} role={role} {...q} />;
  },
});
const applyBookingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/a/$token",
  component: function ApplyBookingRoute(): ReactNode {
    const { token } = applyBookingRoute.useParams();
    return <ApplyBookingPage key={token} token={token} />;
  },
});

// The lead's trial day scorecard, from our text after the trial (Hiring v2 phase 2).
const trialScoreRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/t/$token",
  component: function TrialScoreRoute(): ReactNode {
    const { token } = trialScoreRoute.useParams();
    return <TrialScoreView key={token} token={token} />;
  },
});

const privacy = createRoute({ getParentRoute: () => rootRoute, path: "/privacy", component: Privacy });

const connectClaude = createRoute({ getParentRoute: () => rootRoute, path: "/connect/claude", component: ConnectClaude });

export const publicRoutes = [
  payPage,
  payShort,
  oldInvoiceLink,
  olderInvoiceLink,
  olderQuoteLink,
  reviewPage,
  reviewShort,
  cardSaved,
  saveCard,
  quotePublic,
  quotePreview,
  quoteShort,
  careersRoute,
  careersRoleRoute,
  applyBookingRoute,
  trialScoreRoute,
  reportPublic,
  myTrades,
  unsubscribe,
  asClient,
  privacy,
  connectClaude,
] as const;
